import { useEffect, useRef, useState } from "react";
import { Trash2, Pencil, Star } from "lucide-react";

const SIZE_CLASS = { small: "tile-sm", medium: "tile-md", large: "tile-lg" };

export default function Tile({
  tile,
  size = "medium",
  editing = false,
  onTap,
  onEdit,
  onDelete,
  onToggleFavorite,
  pressed = false,
  dwellMs = 0,
  scanFocused = false,
  categoryColor = null,
  "data-testid": testId,
}) {
  const [holdProgress, setHoldProgress] = useState(0); // 0..1
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const firedRef = useRef(false);

  const cls = `tile-btn no-select ${SIZE_CLASS[size] || SIZE_CLASS.medium} ${
    pressed ? "is-pressed" : ""
  } ${editing ? "edit-wiggle" : ""} ${scanFocused ? "scan-focused" : ""} ${
    tile.is_gestalt ? "is-gestalt" : ""
  }`;
  // Fallback chain: tile's own color → category default → white.
  const bg = tile.color || categoryColor || "#FFFFFF";
  const useDwell = !editing && dwellMs > 0;

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const beginHold = (e) => {
    if (!useDwell) return;
    e.preventDefault();
    firedRef.current = false;
    const start = Date.now();
    const tick = () => {
      const pct = Math.min(1, (Date.now() - start) / dwellMs);
      setHoldProgress(pct);
      if (pct < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      setHoldProgress(0);
      onTap && onTap(tile);
    }, dwellMs);
  };

  const cancelHold = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    timerRef.current = null;
    rafRef.current = null;
    setHoldProgress(0);
  };

  const onClickHandler = () => {
    if (useDwell) return; // dwell mode owns the trigger
    onTap && onTap(tile);
  };

  return (
    <div className="relative" data-testid={testId}>
      <button
        type="button"
        className={cls}
        style={{ background: bg }}
        onClick={onClickHandler}
        onPointerDown={beginHold}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerCancel={cancelHold}
        onContextMenu={(e) => useDwell && e.preventDefault()}
        aria-label={tile.label}
        data-testid={`tile-${tile.id}`}
      >
        {tile.custom_image_b64 ? (
          <img
            src={tile.custom_image_b64}
            alt=""
            className="object-cover rounded-xl"
            style={{ height: "60%", width: "auto", maxWidth: "85%" }}
          />
        ) : tile.image_url ? (
          <img
            src={tile.image_url}
            alt=""
            className="object-contain"
            style={{ height: "55%", width: "auto" }}
          />
        ) : (
          <span className="tile-emoji" aria-hidden>{tile.emoji || "🔤"}</span>
        )}
        <span className="tile-label">{tile.label}</span>
        {tile.custom_audio_b64 && !editing && (
          <span
            className="absolute bottom-1.5 left-1.5 bg-white border-[2px] border-[color:var(--ink)] rounded-full w-6 h-6 flex items-center justify-center"
            aria-label="Has custom voice"
            title="Plays your recorded voice"
          >
            <span className="text-[10px]">🎙️</span>
          </span>
        )}
        {tile.is_favorite && !editing && (
          <span
            className="absolute top-2 right-2 text-[color:var(--coral)]"
            aria-hidden
          >
            <Star size={18} fill="currentColor" strokeWidth={2.5} />
          </span>
        )}
        {tile.is_gestalt && !editing && (
          <span
            className="absolute top-2 left-2 bg-[#A78BFA] text-white border-[2px] border-[color:var(--ink)] rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none"
            aria-label="Gestalt phrase"
            title="Whole-phrase script — speaks as one unit"
          >
            🗯️ script
          </span>
        )}
        {/* Dwell progress bar — only visible while holding */}
        {useDwell && holdProgress > 0 && (
          <span
            aria-hidden
            className="absolute left-0 bottom-0 h-1.5 bg-[color:var(--ink)] rounded-b"
            style={{ width: `${holdProgress * 100}%`, transition: "width 50ms linear" }}
          />
        )}
      </button>

      {editing && (
        <div className="absolute -top-3 -right-3 flex gap-1.5">
          <button
            type="button"
            className="bg-white border-[3px] border-[color:var(--ink)] rounded-full w-9 h-9 flex items-center justify-center shadow-[3px_3px_0_0_var(--ink)] hover:bg-[color:var(--gold)]"
            onClick={() => onToggleFavorite && onToggleFavorite(tile)}
            aria-label="Toggle favorite"
            data-testid={`tile-favorite-${tile.id}`}
          >
            <Star size={16} fill={tile.is_favorite ? "currentColor" : "none"} className={tile.is_favorite ? "text-[color:var(--coral)]" : "text-[color:var(--ink)]"} />
          </button>
          <button
            type="button"
            className="bg-[color:var(--gold)] border-[3px] border-[color:var(--ink)] rounded-full w-9 h-9 flex items-center justify-center shadow-[3px_3px_0_0_var(--ink)]"
            onClick={() => onEdit && onEdit(tile)}
            aria-label="Edit tile"
            data-testid={`tile-edit-${tile.id}`}
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            className="bg-[color:var(--coral)] text-white border-[3px] border-[color:var(--ink)] rounded-full w-9 h-9 flex items-center justify-center shadow-[3px_3px_0_0_var(--ink)]"
            onClick={() => onDelete && onDelete(tile)}
            aria-label="Delete tile"
            data-testid={`tile-delete-${tile.id}`}
          >
            <Trash2 size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
